// ink-component
import React from 'react';
import { ThemeProvider } from 'styled-components';
// eslint-disable-next-line import/no-unresolved
import * as CSS from 'csstype';
import Loader from '../../components/Loader/Loader';
import { SpreadsheetMenu, SpreadsheetSlider, SpreadsheetWrapper } from './components';
import { useSpreadsheetCurrencyLocales } from './hooks/useSpreadsheetCurrencyLocales';
import { useSliderState, ERRORS_TAB_ID } from './hooks/useSliderState';
import {
  AfterChangeCallbackType,
  ErrorType,
  HandsontableGridTypes,
  HotTableType,
  HTMLDivElementType,
  OnSliderErrorsSubmitType,
  SliderPropType,
  SpreadsheetCallbackType,
  SpreadsheetColumnType,
  SpreadsheetMenuType,
  SpreadsheetSettingsType,
} from './types';
import { useSpreadsheetCallbacks } from './hooks/useSpreadsheetCallbacks';

const SpreadsheetTable = React.lazy(() => import('./components/SpreadsheetTable'));

export interface SpreadsheetV2Props extends Omit<HandsontableGridTypes, 'afterChange'> {
  /** Callback fired after changes to the spreadsheet. This callback receives a
   * `dataStateManager` used to reflect Handsontable internal data in the user defined state and
   * perform programatic changes, for example, automatically completing a cell based on a change to
   * the row. See samples for implementation details. */
  afterChange: AfterChangeCallbackType;
  /** Array of external errors */
  errors?: ErrorType[];
  /** Runs after the Spreadsheet's first render. Useful for externalizing access to the callbacks object. */
  onLoad?: (callbacks: SpreadsheetCallbackType) => void;
  /** Validate spreadsheet state on first load */
  validateOnLoad?: boolean;
  /** Array of objects representing column configuration settings. */
  columns: SpreadsheetColumnType[];
  /** Defines the height of SpreadsheetV2 component. The presets 'short', 'medium' and 'tall' show 18, 24 and 36 rows respectively, including the header row. */
  height?: 'short' | 'medium' | 'tall' | 'full';
  /** Defines the height of UI that surrounds the spreadsheet. Used with the 'full' height option */
  heightOffset?: CSS.Properties['height'];
  /** SpreadsheetV2 unique identifier */
  id: string;
  /** Render prop that passes a `callbacks` object and returns a component that will be the child of SpreadsheetMenu. */
  menu?: SpreadsheetMenuType;
  /** Table settings. Extends Handsontable settings. */
  settings?: SpreadsheetSettingsType;
  /** Array of objects containing `id`, `icon`, `buttonLabel` (for button accessibility) and `components` that will render the tabs and tab
   * links of the SpreadsheetSlider */
  slider?: SliderPropType;
  /** Show errors tab in sidebar slider. */
  showSliderErrorsTab?: boolean;
  /** Callback for when the "Submit" button in the errors slider tab is clicked */
  onSliderErrorsSubmit?: OnSliderErrorsSubmitType;
  /** Callback for when the open/closed state of the slider changes */
  onSliderToggle?: (open: boolean) => void;
  /** Collapse internal whitespace in pasted data ("john\n   doe" -> "john doe" ) */
  collapseClipboardWhitespace?: boolean;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
}

const useOnSliderToggle = (open: boolean, onToggle: SpreadsheetV2Props['onSliderToggle']) => {
  const prevOpen = React.useRef(open);

  React.useEffect(() => {
    if (onToggle && prevOpen.current !== open) {
      onToggle(open);
      prevOpen.current = open;
    }
  }, [open, onToggle]);
};

/**
 * @status beta
 * @purpose Allows users to handle large amounts of data
 * @categories spreadsheet
 * @aka handsontable
 */
function SpreadsheetV2(props: SpreadsheetV2Props) {
  const [initialData] = React.useState(() => props.data);
  const [settings] = React.useState(() => props.settings);
  const [columns] = React.useState(() => props.columns);
  const [afterChange] = React.useState(() => props.afterChange);

  // This makes the SpreadsheetSlider re-render whenever the selection changes. Jira code: INK-606
  const [selected, setSelected] = React.useState<number[]>([]);

  const hotTableRef = React.useRef<HotTableType>(null);
  const spreadsheetWrapperRef = React.useRef<HTMLDivElementType>(null);
  const tableContainerRef = React.useRef<HTMLDivElementType>(null);

  const { id, menu, height, heightOffset, slider, onLoad } = props;

  const sliderState = useSliderState({
    /* Slider starts closed */
    open: false,
    /* The initial active tab is the first on the list unless there's only the errors tab */
    activeTab: slider?.length ? slider[0].id : ERRORS_TAB_ID,
  });

  useOnSliderToggle(sliderState.open, props.onSliderToggle);

  const callbacks = useSpreadsheetCallbacks(hotTableRef, columns, sliderState, onLoad);

  const currencyLocales = React.useMemo(
    () =>
      columns
        .filter(col => col.type === 'currency' && col?.numericFormat?.culture)
        .map(col => col.numericFormat!.culture!),
    [columns],
  );

  const doneLoadingLocales = useSpreadsheetCurrencyLocales(currencyLocales);

  if (!doneLoadingLocales) {
    /*
      If there are any locales to load, we need to wait for them to finish before
      rendering the table, because Handsontable won't re-render if this happens after
      the initial load. If there are no locales to load, the table renders immediately.
    */
    return null;
  }

  return (
    <ThemeProvider
      theme={{
        height,
        heightOffset,
        hasMenu: !!menu,
        hasSlider: !!slider || props.showSliderErrorsTab,
        hasRequired: columns.some(col => col.required),
        hasFixedColumns: settings?.fixedColumnsLeft,
      }}
    >
      <SpreadsheetWrapper id={id} ref={spreadsheetWrapperRef} data-testid={props['data-testid']}>
        {menu && callbacks && (
          <SpreadsheetMenu id={`${id}-menu`} data-testid={`${id}-menu`} callbacks={callbacks}>
            {menu}
          </SpreadsheetMenu>
        )}
        <React.Suspense fallback={<Loader isLoading />}>
          <SpreadsheetTable
            id={`${id}-table`}
            data-testid={`${id}-table`}
            columns={columns}
            data={initialData}
            errors={props.errors}
            settings={settings}
            hotTableRef={hotTableRef}
            spreadsheetWrapperRef={spreadsheetWrapperRef}
            tableContainerRef={tableContainerRef}
            afterSelectionEnd={setSelected}
            afterChange={afterChange}
            validateOnLoad={props.validateOnLoad}
            collapseClipboardWhitespace={props.collapseClipboardWhitespace}
          />
        </React.Suspense>
        {(slider?.length || props.showSliderErrorsTab) && callbacks && (
          <SpreadsheetSlider
            id={`${id}-slider`}
            data-testid={`${id}-slider`}
            state={sliderState}
            contents={slider}
            selected={selected}
            spreadsheetWrapperRef={spreadsheetWrapperRef}
            hotTableRef={hotTableRef}
            callbacks={callbacks}
            columns={columns}
            showSliderErrorsTab={props.showSliderErrorsTab}
            onSliderErrorsSubmit={props.onSliderErrorsSubmit}
          />
        )}
      </SpreadsheetWrapper>
    </ThemeProvider>
  );
}

SpreadsheetV2.defaultProps = {
  'data-testid': undefined,
  afterChange: undefined,
  validateOnLoad: false,
  columns: [],
  data: undefined,
  height: 'medium',
  heightOffset: '0',
  id: undefined,
  menu: undefined,
  settings: undefined,
  slider: undefined,
  showSliderErrorsTab: false,
};

SpreadsheetV2.displayName = 'SpreadsheetV2';

export default SpreadsheetV2;
